<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { auth, db, collection, query, where, onSnapshot, orderBy, getDoc, doc } from "@/firebase";
import { onAuthStateChanged, type User } from "firebase/auth";
import type { QuerySnapshot, DocumentData } from "firebase/firestore";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";
import type { PostTestRecord } from "@/services/postTestService";
import TestScoreChart from "./TestScoreChart.vue";

// PreTest interface - knowledge-based assessment
interface PreTestRecord {
  sessionId: string;
  userId: string;
  subject: string;
  topic: string;
  date_completed: any; // ✅ FIX: Use date_completed to match Firebase field name
  score: number; // Number of correct answers
  totalQuestions: number;
  percentage: number; // Score as percentage (0-100)
  questions: any[];
  language: string;
  sources: string[];
}

// Type definition for period
type PeriodType = "day" | "week" | "month" | "year";

// Reactive period state
const selectedPeriod = ref<PeriodType>("week");
const chartRef = ref<InstanceType<typeof TestScoreChart> | null>(null);

// ✅ NEW: Subject filter state
const selectedSubject = ref<string>("all");

// ✅ UPDATED: Complete Philippine K-12 SHS subjects with exact matching
const subjects = [
  { id: "all", label: "All Subjects" },
  { id: "Oral Communication", label: "Oral Communication" },
  { id: "Reading and Writing", label: "Reading and Writing" },
  {
    id: "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
    label: "Komunikasyon at Pananaliksik",
  },
  {
    id: "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
    label: "Pagbasa at Pagsusuri",
  },
  {
    id: "21st Century Literature from the Philippines and the World",
    label: "21st Century Literature",
  },
  { id: "General Mathematics", label: "General Mathematics" },
  { id: "Statistics and Probability", label: "Statistics and Probability" },
  { id: "Earth and Life Science", label: "Earth and Life Science" },
  { id: "Physical Science", label: "Physical Science" },
  { id: "Earth Science", label: "Earth Science" },
] as const;

// ✅ IMPROVED: Tab configuration with more period options and better labels
const tabs: Array<{ id: PeriodType; label: string; description: string }> = [
  { id: "day", label: "Last 7 Days", description: "Recent Week" },
  { id: "week", label: "Last Month", description: "Past Weeks" },
  { id: "month", label: "Last 3 Months", description: "Recent Months" },
  { id: "year", label: "Last 12 Months", description: "Past Year" },
];

// Firebase data
const postTests = ref<PostTestRecord[]>([]);
const preTests = ref<PreTestRecord[]>([]);
const isLoading = ref(true);
const currentUserId = ref<string | null>(null);
const hasError = ref(false);
let unsubscribeFirestore: (() => void) | null = null;
let unsubscribePreTestFirestore: (() => void) | null = null;
let unsubscribeAuth: (() => void) | null = null;

// Set up Firebase listeners
const setupFirebaseListeners = () => {
  logDebug("StudentAnalytics: Setting up Firebase authentication listener");
  unsubscribeAuth = onAuthStateChanged(auth, (user: User | null) => {
    if (user) {
      logInfo("StudentAnalytics: User authenticated, setting up post-test and pre-test listeners");
      currentUserId.value = user.uid;
      setupPostTestsListener();
      setupPreTestsListener();
    } else {
      logInfo("StudentAnalytics: User not authenticated, clearing data");
      currentUserId.value = null;
      postTests.value = [];
      preTests.value = [];
      isLoading.value = false;
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
        unsubscribeFirestore = null;
      }
      if (unsubscribePreTestFirestore) {
        unsubscribePreTestFirestore();
        unsubscribePreTestFirestore = null;
      }
    }
  });
};

const setupPostTestsListener = () => {
  if (!currentUserId.value) return;

  logDebug("StudentAnalytics: Setting up Firestore post-tests listener");

  try {
    const postTestsRef = collection(db, "post_tests");
    const postTestsQuery = query(
      postTestsRef,
      where("user_id", "==", currentUserId.value),
      orderBy("date_completed", "desc")
    );

    unsubscribeFirestore = onSnapshot(
      postTestsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("StudentAnalytics: Post-tests snapshot received", { count: snapshot.size });

        const tests: PostTestRecord[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as PostTestRecord;
          tests.push({
            ...data,
            post_test_id: doc.id,
          });
        });

        postTests.value = tests;
        isLoading.value = false;
        hasError.value = false;
        logSuccess("StudentAnalytics: Post-tests loaded successfully", { count: tests.length });
      },
      (error) => {
        if (error.code === "failed-precondition") {
          setupPostTestsListenerWithoutOrder();
        } else {
          logError("StudentAnalytics: Error fetching post-tests", error);
          isLoading.value = false;
          hasError.value = true;
        }
      }
    );
  } catch (error) {
    logError("StudentAnalytics: Error setting up post-tests listener", error);
    isLoading.value = false;
    hasError.value = true;
  }
};

const setupPostTestsListenerWithoutOrder = () => {
  if (!currentUserId.value) return;

  logInfo("StudentAnalytics: Using client-side sorting for post-tests");

  try {
    const postTestsRef = collection(db, "post_tests");
    const postTestsQuery = query(postTestsRef, where("user_id", "==", currentUserId.value));

    unsubscribeFirestore = onSnapshot(
      postTestsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const tests: PostTestRecord[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as PostTestRecord;
          tests.push({
            ...data,
            post_test_id: doc.id,
          });
        });

        // Sort client-side by date_completed
        tests.sort((a, b) => {
          const aTime = a.date_completed?.toDate?.().getTime() || 0;
          const bTime = b.date_completed?.toDate?.().getTime() || 0;
          return bTime - aTime;
        });

        postTests.value = tests;
        isLoading.value = false;
        hasError.value = false;
        logSuccess("StudentAnalytics: Post-tests loaded with client-side sorting", {
          count: tests.length,
        });
      },
      (error) => {
        logError("StudentAnalytics: Error fetching post-tests without orderBy", error);
        isLoading.value = false;
        hasError.value = true;
      }
    );
  } catch (error) {
    logError("StudentAnalytics: Error setting up listener without orderBy", error);
    isLoading.value = false;
    hasError.value = true;
  }
};

// Set up PreTests listener
const setupPreTestsListener = () => {
  if (!currentUserId.value) return;

  logDebug("StudentAnalytics: Setting up Firestore pre-tests listener");

  try {
    const preTestsRef = collection(db, "preTests");
    const preTestsQuery = query(preTestsRef, where("userId", "==", currentUserId.value));

    unsubscribePreTestFirestore = onSnapshot(
      preTestsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("StudentAnalytics: Pre-tests snapshot received", { count: snapshot.size });

        const tests: PreTestRecord[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as PreTestRecord;

          // ✅ DEBUG: Log raw Firebase data to see actual field values
          console.log("🔍 RAW PRE-TEST DATA FROM FIREBASE:", {
            docId: doc.id,
            sessionId: data.sessionId,
            subject: data.subject,
            topic: data.topic,
            score: data.score,
            totalQuestions: data.totalQuestions,
            percentage: data.percentage,
            hasPercentageField: "percentage" in data,
            percentageType: typeof data.percentage,
            percentageValue: data.percentage,
            calculatedPercentage:
              data.totalQuestions > 0 ? (data.score / data.totalQuestions) * 100 : 0,
          });

          tests.push(data);
        });

        preTests.value = tests;
        logSuccess("StudentAnalytics: Pre-tests loaded successfully", { count: tests.length });
      },
      (error) => {
        logError("StudentAnalytics: Error fetching pre-tests", error);
      }
    );
  } catch (error) {
    logError("StudentAnalytics: Error setting up pre-tests listener", error);
  }
};

// Lifecycle hooks
onMounted(() => {
  logInfo("StudentAnalytics: Component mounted, initializing listeners");
  setupFirebaseListeners();
});

onUnmounted(() => {
  logInfo("StudentAnalytics: Component unmounting, cleaning up listeners");
  if (unsubscribeFirestore) unsubscribeFirestore();
  if (unsubscribePreTestFirestore) unsubscribePreTestFirestore();
  if (unsubscribeAuth) unsubscribeAuth();
});

// Filter tests by selected period AND subject
// NEW: Filtered pre-tests based on subject and period
const filteredPreTests = computed(() => {
  if (preTests.value.length === 0) {
    logDebug("StudentAnalytics: No pre-tests available");
    return [];
  }

  const now = new Date();

  const filtered = preTests.value.filter((test) => {
    const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();
    const timeDiff = now.getTime() - testDate.getTime();

    // FILTER BY SUBJECT: Skip if subject doesn't match (unless "all" selected)
    if (selectedSubject.value !== "all" && test.subject !== selectedSubject.value) {
      return false;
    }

    // ✅ IMPROVED: Filter by period with accurate time ranges
    switch (selectedPeriod.value) {
      case "day":
        return timeDiff <= 7 * 24 * 60 * 60 * 1000; // Last 7 days
      case "week":
        return timeDiff <= 30 * 24 * 60 * 60 * 1000; // Last 30 days (1 month)
      case "month":
        return timeDiff <= 90 * 24 * 60 * 60 * 1000; // Last 90 days (3 months)
      case "year":
        return timeDiff <= 365 * 24 * 60 * 60 * 1000; // Last 365 days (12 months)
      default:
        return true;
    }
  });

  logDebug("StudentAnalytics: Filtered pre-tests", {
    total: preTests.value.length,
    filtered: filtered.length,
    period: selectedPeriod.value,
    subject: selectedSubject.value,
  });

  return filtered;
});

const filteredTests = computed(() => {
  if (postTests.value.length === 0) {
    logDebug("StudentAnalytics: No post-tests available");
    return [];
  }

  const now = new Date();

  // ✅ DEBUG: Log all test subjects first
  logDebug("StudentAnalytics: All test subjects", {
    tests: postTests.value.map((t) => ({
      id: t.post_test_id,
      subject: t.subject,
      score: t.score,
    })),
  });

  const filtered = postTests.value.filter((test) => {
    const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();
    const timeDiff = now.getTime() - testDate.getTime();

    // ✅ FILTER BY SUBJECT: Skip if subject doesn't match (unless "all" selected)
    if (selectedSubject.value !== "all" && test.subject !== selectedSubject.value) {
      logDebug("StudentAnalytics: Filtering out test due to subject mismatch", {
        testId: test.post_test_id,
        testSubject: test.subject,
        selectedSubject: selectedSubject.value,
      });
      return false;
    }

    // ✅ IMPROVED: Filter by time period with accurate ranges
    switch (selectedPeriod.value) {
      case "day":
        // Last 7 days
        return timeDiff >= 0 && timeDiff <= 7 * 24 * 60 * 60 * 1000;
      case "week":
        // Last 30 days (1 month)
        return timeDiff >= 0 && timeDiff <= 30 * 24 * 60 * 60 * 1000;
      case "month":
        // Last 90 days (3 months)
        return timeDiff >= 0 && timeDiff <= 90 * 24 * 60 * 60 * 1000;
      case "year":
        // Last 365 days (12 months)
        return timeDiff >= 0 && timeDiff <= 365 * 24 * 60 * 60 * 1000;
      default:
        return true;
    }
  });

  logDebug("StudentAnalytics: Filtered tests", {
    period: selectedPeriod.value,
    subject: selectedSubject.value,
    total: postTests.value.length,
    filtered: filtered.length,
    filteredTests: filtered.map((t) => ({
      id: t.post_test_id,
      subject: t.subject,
      score: t.score,
    })),
  });

  return filtered;
});

// ✅ NEW: Computed statistics showing separate averages for pre-tests and post-tests
const statistics = computed(() => {
  const postTestsData = filteredTests.value;
  const preTestsData = filteredPreTests.value;

  // ✅ DEBUG: Log filtered data for each period
  logDebug("StudentAnalytics: Statistics calculation", {
    period: selectedPeriod.value,
    filteredPostTests: postTestsData.length,
    filteredPreTests: preTestsData.length,
    totalPostTests: postTests.value.length,
    totalPreTests: preTests.value.length,
    postTestDates: postTestsData.map((t) => ({
      id: t.post_test_id,
      date: t.date_completed?.toDate?.() || "No date",
      score: t.score,
    })),
  });

  // Calculate average pre-test score
  let avgPreScore = 0;
  if (preTestsData.length > 0) {
    const preScores = preTestsData
      .map((test) => {
        // PreTests always have percentage field (0-100)
        const pct = test.percentage;
        return typeof pct === "number" && !isNaN(pct) && pct >= 0 && pct <= 100 ? pct : null;
      })
      .filter((score): score is number => score !== null);

    avgPreScore =
      preScores.length > 0
        ? Math.round(preScores.reduce((sum, score) => sum + score, 0) / preScores.length)
        : 0;

    logDebug("Pre-test score calculation", {
      totalTests: preTestsData.length,
      validScores: preScores.length,
      scores: preScores,
      average: avgPreScore,
    });
  }

  // Calculate average post-test score
  // ✅ STRICT: Only use percentage field from Firebase (0-100 scale)
  let avgPostScore = 0;
  if (postTestsData.length > 0) {
    const postScores = postTestsData
      .map((test) => {
        // PostTests should have percentage field (0-100)
        // For legacy records without percentage, calculate from score
        let pct: number;
        if (test.percentage !== undefined && test.percentage !== null) {
          pct = test.percentage;
        } else if (test.score !== undefined && test.score !== null) {
          // Legacy: if score is 0-10, convert to percentage
          pct = test.score <= 10 ? test.score * 10 : test.score;
        } else {
          return null;
        }
        return typeof pct === "number" && !isNaN(pct) && pct >= 0 && pct <= 100 ? pct : null;
      })
      .filter((score): score is number => score !== null);

    avgPostScore =
      postScores.length > 0
        ? Math.round(postScores.reduce((sum, score) => sum + score, 0) / postScores.length)
        : 0;

    logDebug("Post-test score calculation", {
      totalTests: postTestsData.length,
      validScores: postScores.length,
      scores: postScores,
      average: avgPostScore,
    });
  }

  // Calculate improvement (avgPostScore - avgPreScore), capped at 100%
  let improvement = 0;
  if (avgPreScore > 0 && avgPostScore > 0) {
    improvement = Math.min(100, avgPostScore - avgPreScore);
  }

  // Get highest score from BOTH pre-tests and post-tests
  // ✅ UPDATED: Check both test types to find the absolute highest score
  let highestScore = 0;

  // Get all post-test scores
  const postScores = postTestsData
    .map((test) => {
      let pct: number;
      if (test.percentage !== undefined && test.percentage !== null) {
        pct = test.percentage;
      } else if (test.score !== undefined && test.score !== null) {
        pct = test.score <= 10 ? test.score * 10 : test.score;
      } else {
        return null;
      }
      return typeof pct === "number" && !isNaN(pct) && pct >= 0 && pct <= 100 ? pct : null;
    })
    .filter((score): score is number => score !== null);

  // Get all pre-test scores
  const preScores = preTestsData
    .map((test) => {
      const pct = test.percentage;
      return typeof pct === "number" && !isNaN(pct) && pct >= 0 && pct <= 100 ? pct : null;
    })
    .filter((score): score is number => score !== null);

  // Combine all scores and find the maximum
  const allScores = [...postScores, ...preScores];
  highestScore = allScores.length > 0 ? Math.max(...allScores) : 0;

  logDebug("Highest score calculation (pre-tests + post-tests)", {
    postTestScores: postScores.length,
    preTestScores: preScores.length,
    totalScores: allScores.length,
    highest: highestScore,
  });

  // Total tests taken (combined pre-tests and post-tests)
  const totalTests = preTestsData.length + postTestsData.length;

  logInfo("StudentAnalytics: Statistics computed", {
    period: selectedPeriod.value,
    avgPreScore,
    avgPostScore,
    improvement,
    highestScore,
    totalTests,
  });

  return {
    avgPreScore,
    avgPostScore,
    improvement,
    improvementPercentage: improvement, // Same as improvement since already calculated as difference
    totalTests,
    highestScore,
    totalSessions: totalTests,
  };
});

// Function to change period
const changePeriod = (period: PeriodType) => {
  selectedPeriod.value = period;
};

// ✅ NEW: Function to change subject filter
const changeSubject = (subjectId: string) => {
  selectedSubject.value = subjectId;
  logInfo("StudentAnalytics: Subject filter changed", { subject: subjectId });
};

// Match preTests with postTests by sessionId for knowledge-based comparison
const matchedTestPairs = computed(() => {
  const pairs: Array<{
    sessionId: string;
    subject: string;
    preTest: PreTestRecord;
    postTest: PostTestRecord;
    preScore: number; // Pre-test percentage (0-100)
    postScore: number; // Post-test percentage (0-100)
    improvement: number; // Absolute improvement (post - pre)
    improvementPercentage: number; // Percentage improvement
  }> = [];

  logDebug("StudentAnalytics: Attempting to match preTests with postTests", {
    totalPostTests: filteredTests.value.length,
    totalPreTests: filteredPreTests.value.length,
    postTestSessionIds: filteredTests.value.map((pt) => pt.session_id),
    preTestSessionIds: filteredPreTests.value.map((pt) => pt.sessionId),
  });

  filteredTests.value.forEach((postTest) => {
    const preTest = filteredPreTests.value.find((pt) => pt.sessionId === postTest.session_id);

    logDebug("StudentAnalytics: Matching attempt", {
      postTestSessionId: postTest.session_id,
      postTestSubject: postTest.subject,
      preTestFound: !!preTest,
      preTestSessionId: preTest?.sessionId,
    });

    // ✅ CRITICAL: Skip if pre-test exists but has no valid data (placeholder records)
    if (
      preTest &&
      (preTest.score === undefined ||
        preTest.totalQuestions === undefined ||
        preTest.totalQuestions === 0)
    ) {
      logDebug("StudentAnalytics: Skipping incomplete pre-test (placeholder record)", {
        sessionId: postTest.session_id,
        hasScore: preTest.score !== undefined,
        hasTotalQuestions: preTest.totalQuestions !== undefined,
        totalQuestions: preTest.totalQuestions,
      });
      return; // Skip this pair
    }

    if (preTest) {
      // ✅ CRITICAL FIX: Calculate percentage from score/totalQuestions if percentage field is missing
      // Pre-tests store: score (number correct) and totalQuestions (total questions)
      // We need to calculate: (score / totalQuestions) * 100

      console.log("🎯 MATCHING PRE-TEST FOUND:", {
        sessionId: postTest.session_id,
        preTestRawData: {
          percentage: preTest.percentage,
          percentageType: typeof preTest.percentage,
          hasPercentageField: "percentage" in preTest,
          score: preTest.score,
          totalQuestions: preTest.totalQuestions,
        },
      });

      let preScore = 0;
      if (preTest.percentage !== undefined && preTest.percentage !== null) {
        // If percentage field exists, use it
        preScore = preTest.percentage;
        console.log("✅ Using percentage field:", preScore);
      } else if (preTest.totalQuestions > 0) {
        // Calculate percentage from score and totalQuestions
        preScore = (preTest.score / preTest.totalQuestions) * 100;
        console.log("🔢 Calculated from score/total:", preScore);
      } else {
        console.log("❌ No valid data to calculate percentage");
      }

      // ✅ CRITICAL FIX: Use percentage field if available (matches preTests format)
      // Fallback to score conversion for backward compatibility
      let postScore = 0;
      if (postTest.percentage !== undefined && postTest.percentage !== null) {
        // New format with percentage field (0-100)
        postScore = postTest.percentage;
      } else {
        // Legacy format or old migrated data: convert score
        const rawPostScore = postTest.score ?? 0;
        postScore = rawPostScore <= 10 ? rawPostScore * 10 : rawPostScore;
      }
      const improvement = postScore - preScore; // Absolute improvement
      const improvementPercentage = preScore > 0 ? (improvement / preScore) * 100 : 0; // Percentage change

      // ✅ DEBUG: Log actual pre-test data to verify field values
      logDebug("StudentAnalytics: Pre-test data inspection", {
        sessionId: postTest.session_id,
        preTestPercentage: preTest.percentage,
        preTestScore: preTest.score,
        preTestTotalQuestions: preTest.totalQuestions,
        calculatedPreScore: preScore,
        postTestScore: postScore,
        improvement: improvement,
      });

      // Only add if we have valid scores
      if (typeof preScore === "number" && typeof postScore === "number") {
        pairs.push({
          sessionId: postTest.session_id,
          subject: postTest.subject,
          preTest,
          postTest,
          preScore: parseFloat(preScore.toFixed(1)),
          postScore: parseFloat(postScore.toFixed(1)),
          improvement: parseFloat(improvement.toFixed(1)),
          improvementPercentage: parseFloat(improvementPercentage.toFixed(1)),
        });

        logSuccess("StudentAnalytics: Successfully matched pair", {
          sessionId: postTest.session_id,
          subject: postTest.subject,
          preScore: preScore.toFixed(1) + "%",
          postScore: postScore.toFixed(1) + "%",
          improvement: improvement.toFixed(1) + "%",
          improvementPercentage: improvementPercentage.toFixed(1) + "%",
        });
      } else {
        logDebug("StudentAnalytics: Skipping pair due to invalid scores", {
          sessionId: postTest.session_id,
          preScore,
          postScore,
          preTestData: preTest,
        });
      }
    }
  });

  logInfo("StudentAnalytics: Final matched pairs", {
    count: pairs.length,
    pairs: pairs.map((p) => ({
      sessionId: p.sessionId,
      subject: p.subject,
      preScore: p.preScore,
      postScore: p.postScore,
      improvement: p.improvement,
    })),
  });

  return pairs;
});

// STRICTLY COMPARE PRE-TEST vs POST-TEST AVERAGES
const allTimeMetrics = computed(() => {
  // Use ALL tests (not filtered by period) for the learning journey
  const allPostTests = postTests.value;
  const allPreTests = preTests.value;

  // If no post-tests available, return empty state
  if (allPostTests.length === 0) {
    return {
      avgPreScore: 0,
      avgPostScore: 0,
      avgImprovement: 0,
      totalPairs: 0,
      totalCorrect: 0,
      totalQuestions: 0,
      improvementRate: 0,
      progressBadge: "No Tests Yet",
      badgeColor: "gray",
      celebrationMessage: "Start Your Learning Journey",
      encouragementMessage: "Complete tests to track your progress!",
    };
  }

  // CALCULATE AVERAGE PRE-TEST SCORE (from ALL pre-tests)
  let avgPreScore = 0;
  let preTestCount = 0;
  if (allPreTests.length > 0) {
    const validPreScores = allPreTests
      .filter((test) => test.totalQuestions > 0 && test.score >= 0)
      .map((test) => {
        const pct = test.percentage || (test.score / test.totalQuestions) * 100;
        return typeof pct === "number" && !isNaN(pct) && pct >= 0 && pct <= 100 ? pct : null;
      })
      .filter((score): score is number => score !== null);

    if (validPreScores.length > 0) {
      avgPreScore = parseFloat(
        (validPreScores.reduce((sum, score) => sum + score, 0) / validPreScores.length).toFixed(1)
      );
      preTestCount = validPreScores.length;
    }
  }

  // CALCULATE AVERAGE POST-TEST SCORE (from ALL post-tests)
  let avgPostScore = 0;
  let postTestCount = 0;
  const validPostScores = allPostTests
    .map((test) => {
      let pct: number;
      if (test.percentage !== undefined && test.percentage !== null) {
        pct = test.percentage;
      } else if (test.score !== undefined && test.score !== null) {
        pct = test.score <= 10 ? test.score * 10 : test.score;
      } else {
        return null;
      }
      return typeof pct === "number" && !isNaN(pct) && pct >= 0 && pct <= 100 ? pct : null;
    })
    .filter((score): score is number => score !== null);

  if (validPostScores.length > 0) {
    avgPostScore = parseFloat(
      (validPostScores.reduce((sum, score) => sum + score, 0) / validPostScores.length).toFixed(1)
    );
    postTestCount = validPostScores.length;
  }

  // CALCULATE IMPROVEMENT (always calculate, even if pre-test is 0)
  const avgImprovement = parseFloat((avgPostScore - avgPreScore).toFixed(1));

  // ESTIMATE total correct/total questions
  const totalCorrect = Math.round(validPostScores.reduce((sum, score) => sum + score / 10, 0));
  const totalQuestions = allPostTests.length * 10;

  // Match pre-tests with post-tests for improvement rate
  const allPairs: Array<{
    preScore: number;
    postScore: number;
    improvement: number;
    correctAnswers: number;
    totalQuestions: number;
  }> = [];

  allPostTests.forEach((postTest) => {
    const preTest = allPreTests.find((pt) => pt.sessionId === postTest.session_id);

    if (preTest && preTest.totalQuestions > 0 && preTest.score >= 0) {
      // Validate pre-test percentage
      const prePercentage = preTest.percentage;
      if (
        typeof prePercentage !== "number" ||
        isNaN(prePercentage) ||
        prePercentage < 0 ||
        prePercentage > 100
      ) {
        logDebug("Skipping invalid pre-test data", {
          sessionId: postTest.session_id,
          prePercentage,
        });
        return;
      }

      // Validate post-test percentage
      let postPercentage: number;
      if (postTest.percentage !== undefined && postTest.percentage !== null) {
        postPercentage = postTest.percentage;
      } else if (postTest.score !== undefined && postTest.score !== null) {
        postPercentage = postTest.score <= 10 ? postTest.score * 10 : postTest.score;
      } else {
        logDebug("Skipping post-test with no valid score", { sessionId: postTest.session_id });
        return;
      }

      if (
        typeof postPercentage !== "number" ||
        isNaN(postPercentage) ||
        postPercentage < 0 ||
        postPercentage > 100
      ) {
        logDebug("Skipping invalid post-test data", {
          sessionId: postTest.session_id,
          postPercentage,
        });
        return;
      }

      const preScore = parseFloat(prePercentage.toFixed(1));
      const postScore = parseFloat(postPercentage.toFixed(1));
      const improvement = parseFloat((postScore - preScore).toFixed(1));

      // Calculate correct answers from score and total questions
      const totalQs = postTest.questions?.length || 10;
      const correctAns = Math.round((postScore / 100) * totalQs);

      allPairs.push({
        preScore,
        postScore,
        improvement,
        correctAnswers: correctAns,
        totalQuestions: totalQs,
      });

      logDebug("Matched test pair", {
        sessionId: postTest.session_id,
        preScore,
        postScore,
        improvement,
      });
    }
  });

  // Calculate improvement rate from matched pairs (if any)
  const sessionsWithImprovement = allPairs.filter((p) => p.improvement > 0).length;
  const improvementRate =
    allPairs.length > 0 ? (sessionsWithImprovement / allPairs.length) * 100 : 0;

  // Progress badge based on improvement
  let progressBadge = "Keep Learning!";
  let badgeColor = "blue";

  if (avgImprovement >= 40) {
    progressBadge = "Outstanding Growth!";
    badgeColor = "purple";
  } else if (avgImprovement >= 30) {
    progressBadge = "Excellent Progress!";
    badgeColor = "emerald";
  } else if (avgImprovement >= 20) {
    progressBadge = "Great Improvement!";
    badgeColor = "green";
  } else if (avgImprovement >= 10) {
    progressBadge = "Steady Growth!";
    badgeColor = "blue";
  } else if (avgImprovement >= 0) {
    progressBadge = "Learning in Progress!";
    badgeColor = "yellow";
  } else {
    progressBadge = "Keep Trying!";
    badgeColor = "gray";
  }

  // Calculate highest score from both pre-tests and post-tests
  const allPreScores = allPreTests
    .filter((test) => test.totalQuestions > 0 && test.score >= 0)
    .map((test) => {
      const pct = test.percentage || (test.score / test.totalQuestions) * 100;
      return typeof pct === "number" && !isNaN(pct) && pct >= 0 && pct <= 100 ? pct : null;
    })
    .filter((score): score is number => score !== null);

  const allPostScores = validPostScores;
  const allScores = [...allPreScores, ...allPostScores];
  const highestScore = allScores.length > 0 ? Math.max(...allScores) : 0;

  // Celebration messages
  let celebrationMessage = "";
  let encouragementMessage = "";

  if (avgImprovement >= 40) {
    celebrationMessage = "Outstanding Growth!";
    encouragementMessage = `You've made incredible progress! Your scores improved by ${avgImprovement}% from pre-test to post-test. Keep up this amazing work!`;
  } else if (avgImprovement >= 30) {
    celebrationMessage = "Excellent Progress!";
    encouragementMessage = `Great job! You improved by ${avgImprovement}% between pre-test and post-test. Your dedication is showing great results!`;
  } else if (avgImprovement >= 20) {
    celebrationMessage = "Great Improvement!";
    encouragementMessage = `You're doing really well! Your scores went up by ${avgImprovement}% from pre-test to post-test. Keep practicing!`;
  } else if (avgImprovement >= 10) {
    celebrationMessage = "Steady Growth!";
    encouragementMessage = `Good progress! You improved by ${avgImprovement}% between tests. Continue working with your tutor to keep improving!`;
  } else if (avgImprovement > 0) {
    celebrationMessage = "Making Progress!";
    encouragementMessage = `You're improving! Your scores went up by ${avgImprovement}%. Every bit of progress counts - keep going!`;
  } else if (avgImprovement === 0) {
    celebrationMessage = "Stay Consistent!";
    encouragementMessage = `You maintained your score across tests. Work with your tutor to identify areas for growth and keep practicing!`;
  } else {
    celebrationMessage = "Keep Building Your Skills!";
    const gap = Math.abs(avgImprovement);
    encouragementMessage = `Learning is a journey with ups and downs. Focus on understanding the material deeply with your tutor. With consistent effort and the right strategies, you'll see your scores improve. Remember, every expert was once a beginner!`;
  }

  // ALWAYS return comparison data - avgPreScore and avgPostScore are calculated above
  return {
    avgPreScore,
    avgPostScore,
    avgImprovement,
    totalPairs: allPostTests.length,
    totalCorrect,
    totalQuestions,
    improvementRate,
    progressBadge,
    badgeColor,
    celebrationMessage,
    encouragementMessage,
    highestScore,
  };
});

// ✅ STUDENT-FRIENDLY: Calculate metrics based on available test data (post-tests OR matched pairs)
const comparisonMetrics = computed(() => {
  const pairs = matchedTestPairs.value;
  const tests = filteredTests.value;

  // Use post-tests if no pairs available (more lenient approach)
  if (pairs.length === 0 && tests.length === 0) {
    return {
      avgPreScore: 0,
      avgPostScore: 0,
      avgImprovement: 0,
      avgImprovementPercentage: 0,
      learningTrend: "N/A",
      teachingInsight: "Take some tests to see your progress!",
      totalPairs: 0,
      sessionsWithImprovement: 0,
      averageGainPerSession: 0,
    };
  }

  // If we have matched pairs, use them for accurate comparison
  if (pairs.length > 0) {
    // Calculate averages
    const avgPreScore = pairs.reduce((sum, p) => sum + p.preScore, 0) / pairs.length;
    const avgPostScore = pairs.reduce((sum, p) => sum + p.postScore, 0) / pairs.length;
    const avgImprovement = pairs.reduce((sum, p) => sum + p.improvement, 0) / pairs.length;

    // ✅ FIXED: Cap improvement percentage at 100% max
    const avgImprovementPercentage = Math.min(
      100,
      pairs.reduce((sum, p) => {
        const remainingGap = 100 - p.preScore;
        const gainPercentage = remainingGap > 0 ? (p.improvement / remainingGap) * 100 : 0;
        return sum + Math.min(100, Math.max(0, gainPercentage));
      }, 0) / pairs.length
    );

    // Count sessions with actual improvement
    const sessionsWithImprovement = pairs.filter((p) => p.improvement > 0).length;
    const averageGainPerSession = avgImprovement;

    // ✅ IMPROVED: More nuanced learning trend based on absolute improvement
    let learningTrend = "Needs Support";
    if (avgImprovement >= 30) learningTrend = "Exceptional Growth";
    else if (avgImprovement >= 20) learningTrend = "Excellent Progress";
    else if (avgImprovement >= 10) learningTrend = "Strong Improvement";
    else if (avgImprovement >= 5) learningTrend = "Steady Growth";
    else if (avgImprovement >= 0) learningTrend = "Minimal Progress";
    else learningTrend = "Needs Support";

    // ✅ STUDENT-FRIENDLY: Progress messages written from student's perspective
    let teachingInsight = "";
    const improvementRate = (sessionsWithImprovement / pairs.length) * 100;

    if (avgImprovement >= 30) {
      teachingInsight = `Amazing work! You improved by an average of ${avgImprovement.toFixed(
        1
      )} points per session (${improvementRate.toFixed(
        0
      )}% of sessions showed improvement). You're learning really well!`;
    } else if (avgImprovement >= 20) {
      teachingInsight = `Great job! You're showing excellent progress with ${avgImprovement.toFixed(
        1
      )} point average improvement. You're retaining what you learn across ${pairs.length} session${
        pairs.length !== 1 ? "s" : ""
      }.`;
    } else if (avgImprovement >= 10) {
      teachingInsight = `You're doing well! You gained ${avgImprovement.toFixed(
        1
      )} points per session. ${sessionsWithImprovement} out of ${pairs.length} session${
        pairs.length !== 1 ? "s" : ""
      } showed good improvement. Keep practicing!`;
    } else if (avgImprovement >= 5) {
      teachingInsight = `You're making steady progress with ${avgImprovement.toFixed(
        1
      )} points per session. Try practicing more between sessions to boost your learning even further!`;
    } else if (avgImprovement >= 0) {
      teachingInsight = `You're showing some improvement (${avgImprovement.toFixed(
        1
      )} points). Keep working at it - learning takes time! Consider asking your tutor for extra practice materials.`;
    } else {
      teachingInsight = `It looks like these topics are challenging for you. Don't worry - that's okay! Talk to your tutor about trying different learning approaches that might work better for you.`;
    }

    // ✅ DEBUG: Log final calculated metrics
    logInfo("StudentAnalytics: Comparison metrics calculated (matched pairs)", {
      avgPreScore: avgPreScore.toFixed(1),
      avgPostScore: avgPostScore.toFixed(1),
      avgImprovement: avgImprovement.toFixed(1),
      avgImprovementPercentage: avgImprovementPercentage.toFixed(1),
      totalPairs: pairs.length,
      sessionsWithImprovement,
      learningTrend,
    });

    return {
      avgPreScore: parseFloat(avgPreScore.toFixed(1)),
      avgPostScore: parseFloat(avgPostScore.toFixed(1)),
      avgImprovement: parseFloat(avgImprovement.toFixed(1)),
      avgImprovementPercentage: parseFloat(avgImprovementPercentage.toFixed(1)),
      learningTrend,
      teachingInsight,
      totalPairs: pairs.length,
      sessionsWithImprovement,
      averageGainPerSession: parseFloat(avgImprovement.toFixed(1)),
    };
  }

  // ✅ NEW: Calculate metrics from post-tests alone when no matched pairs available
  // This handles cases where pre-tests are incomplete/missing
  const sortedTests = tests.slice().sort((a, b) => {
    const aTime = a.date_completed?.toDate?.().getTime() || 0;
    const bTime = b.date_completed?.toDate?.().getTime() || 0;
    return aTime - bTime;
  });
  const avgPostScore = tests.reduce((sum, t) => sum + t.score, 0) / tests.length;

  // Calculate improvement as score progression over time
  const firstScore = sortedTests[0].score;
  const lastScore = sortedTests[sortedTests.length - 1].score;
  const improvement = lastScore - firstScore;

  // Estimate learning trend based on current performance
  let learningTrend = "Building Knowledge";
  if (avgPostScore >= 90) learningTrend = "Excellent Mastery";
  else if (avgPostScore >= 80) learningTrend = "Strong Performance";
  else if (avgPostScore >= 70) learningTrend = "Good Progress";
  else if (avgPostScore >= 60) learningTrend = "Developing Skills";
  else learningTrend = "Building Foundation";

  // Student-friendly insight based on post-test performance
  let teachingInsight = "";
  if (avgPostScore >= 90) {
    teachingInsight = `Excellent work! You're scoring an average of ${avgPostScore.toFixed(
      1
    )}% across ${tests.length} test${tests.length !== 1 ? "s" : ""}. Keep up the great learning!`;
  } else if (avgPostScore >= 80) {
    teachingInsight = `Great job! You're averaging ${avgPostScore.toFixed(
      1
    )}% on your tests. You're showing strong understanding!`;
  } else if (avgPostScore >= 70) {
    teachingInsight = `You're doing well with an average of ${avgPostScore.toFixed(1)}% across ${
      tests.length
    } test${tests.length !== 1 ? "s" : ""}. Keep practicing to get even better!`;
  } else if (avgPostScore >= 60) {
    teachingInsight = `You're making progress with ${avgPostScore.toFixed(
      1
    )}% average. Keep working with your tutor to strengthen your understanding!`;
  } else {
    teachingInsight = `You're building your foundation with ${avgPostScore.toFixed(
      1
    )}% average. Don't give up - learning takes time! Talk to your tutor about extra support.`;
  }

  logInfo("StudentAnalytics: Comparison metrics calculated (post-tests only)", {
    avgPostScore: avgPostScore.toFixed(1),
    improvement: improvement.toFixed(1),
    totalTests: tests.length,
    learningTrend,
  });

  return {
    avgPreScore: 0, // No pre-test data available
    avgPostScore: parseFloat(avgPostScore.toFixed(1)),
    avgImprovement: parseFloat(improvement.toFixed(1)),
    avgImprovementPercentage: 0,
    learningTrend,
    teachingInsight,
    totalPairs: tests.length, // Use test count instead of pair count
    sessionsWithImprovement: improvement > 0 ? 1 : 0,
    averageGainPerSession: parseFloat(improvement.toFixed(1)),
  };
});
</script>

<template>
  <div class="space-y-6">
    <!-- Statistics and Graph Card -->
    <div
      class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
    >
      <div
        class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6"
      >
        <div class="flex items-center">
          <svg
            class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z" />
          </svg>
          <h3 class="text-base sm:text-lg font-semibold text-gray-900">Test Score Analytics</h3>
        </div>

        <!-- ✅ NEW: Subject Filter Dropdown -->
        <div class="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
          <svg
            class="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z"
            />
          </svg>
          <select
            v-model="selectedSubject"
            @change="changeSubject(selectedSubject)"
            class="text-xs sm:text-sm border border-gray-300 rounded-md px-2 sm:px-3 py-1 sm:py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1 sm:flex-initial min-w-0"
          >
            <option v-for="subject in subjects" :key="subject.id" :value="subject.id">
              {{ subject.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <svg
            class="animate-spin h-8 w-8 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Loading Analytics...</h3>
        <p class="text-gray-500">Please wait while we fetch your test data.</p>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M13,17H11V15H13M13,13H11V7H13"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Error Loading Analytics</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">
          Failed to load test analytics. Please try refreshing the page.
        </p>
      </div>

      <!-- Empty State -->
      <div v-else-if="postTests.length === 0" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M7,7H17V5H19V19H5V5H7V7M12,17L7,12L8.41,10.58L12,14.17L15.59,10.58L17,12L12,17Z"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No Tests Taken Yet</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">
          Complete post-tests after tutoring sessions to see your analytics and track your progress.
        </p>
      </div>

      <!-- Analytics Content (when data exists) -->
      <div v-else>
        <!-- ✅ IMPROVED: Period Tabs with better styling and tooltips -->
        <div
          class="flex flex-wrap sm:flex-nowrap gap-2 mb-4 sm:mb-6 p-1 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200"
        >
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="changePeriod(tab.id)"
            :title="tab.description"
            :class="[
              'group relative flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 px-2 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap',
              selectedPeriod === tab.id
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200 transform scale-105'
                : 'bg-white text-gray-700 hover:text-blue-600 hover:bg-blue-50 hover:shadow-md border border-gray-200',
            ]"
          >
            <span class="flex items-center justify-center gap-1.5">
              <!-- Icon for active tab -->
              <svg
                v-if="selectedPeriod === tab.id"
                class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z" />
              </svg>
              {{ tab.label }}
            </span>
            <!-- Tooltip on hover -->
            <span
              class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap"
            >
              {{ tab.description }}
            </span>
          </button>
        </div>

        <!-- No tests in selected period -->
        <div v-if="filteredTests.length === 0" class="text-center py-6 sm:py-8 px-4">
          <div class="text-gray-400 mb-2">
            <svg class="w-10 h-10 sm:w-12 sm:h-12 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3"
              />
            </svg>
          </div>
          <p class="text-sm sm:text-base text-gray-600 font-medium">
            No tests found for {{ selectedSubject !== "all" ? "this subject in" : "" }} this period
          </p>
          <p class="text-xs sm:text-sm text-gray-500 mt-1">
            {{
              selectedSubject !== "all"
                ? "Try selecting a different subject or time period"
                : "Try selecting a different time period to view your test history"
            }}
          </p>
        </div>

        <!-- Statistics and Chart (when tests exist in period) -->
        <div v-else class="w-full overflow-x-hidden max-h-[450px] overflow-y-auto">
          <!-- Chart -->
          <div class="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
            <div class="mb-2 sm:mb-3">
              <h4 class="text-xs sm:text-sm font-medium text-gray-700 mb-1">Score Trends</h4>
              <p class="text-xs text-gray-500">
                Track your pre-test and post-test performance over time
              </p>
            </div>
            <TestScoreChart
              ref="chartRef"
              :period="selectedPeriod"
              :postTests="filteredTests"
              :preTests="filteredPreTests"
            />
          </div>

          <!-- ✅ COMPACT: My Learning Journey - Shows Pre-Test vs Post-Test Comparison -->
          <div
            v-if="allTimeMetrics.totalPairs > 0"
            class="mt-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border-2 border-blue-200 shadow-md animate-fade-in"
          >
            <!-- Header -->
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z"
                  />
                </svg>
                <h3 class="text-base font-bold text-gray-900">My Learning Journey</h3>
              </div>
              <div
                class="px-3 py-1 rounded-full text-xs font-bold"
                :class="{
                  'bg-purple-500 text-white': allTimeMetrics.badgeColor === 'purple',
                  'bg-emerald-500 text-white': allTimeMetrics.badgeColor === 'emerald',
                  'bg-green-500 text-white': allTimeMetrics.badgeColor === 'green',
                  'bg-blue-500 text-white': allTimeMetrics.badgeColor === 'blue',
                  'bg-yellow-500 text-white': allTimeMetrics.badgeColor === 'yellow',
                  'bg-gray-500 text-white': allTimeMetrics.badgeColor === 'gray',
                }"
              >
                {{ allTimeMetrics.progressBadge }}
              </div>
            </div>

            <!-- Score Comparison Box - ALWAYS SHOW PRE-TEST vs POST-TEST COMPARISON -->
            <div class="bg-white rounded-lg p-5 mb-4 border-2 border-gray-200">
              <!-- Title: Pre-Test vs Post-Test Comparison -->
              <div class="text-center mb-4">
                <h4 class="text-xl font-bold text-gray-900 mb-2">
                  Pre-Test vs Post-Test Comparison
                </h4>
                <p class="text-sm text-gray-600">
                  Average across {{ allTimeMetrics.totalPairs }} learning session{{
                    allTimeMetrics.totalPairs !== 1 ? "s" : ""
                  }}
                </p>
              </div>

              <!-- Pre-Test vs Post-Test Comparison - Side by Side -->
              <div class="grid grid-cols-2 gap-6 items-center mb-4">
                <!-- Pre-Test Score -->
                <div class="text-center">
                  <p class="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                    Before Tutoring
                  </p>
                  <div class="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                    <div class="text-4xl font-black text-blue-600 mb-1">
                      {{ Math.round(allTimeMetrics.avgPreScore) }}%
                    </div>
                    <p class="text-xs text-gray-600">Pre-Test Average</p>
                  </div>
                </div>

                <!-- Post-Test Score -->
                <div class="text-center">
                  <p class="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                    After Tutoring
                  </p>
                  <div class="bg-purple-50 rounded-xl p-4 border-2 border-purple-300">
                    <div class="text-4xl font-black text-purple-600 mb-1">
                      {{ Math.round(allTimeMetrics.avgPostScore) }}%
                    </div>
                    <p class="text-xs text-gray-600">Post-Test Average</p>
                  </div>
                </div>
              </div>

              <!-- Highest Score (Full Width) -->
              <div
                class="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-6 border-2 border-amber-300 transition-all duration-300 hover:shadow-lg hover:scale-105"
              >
                <div class="text-center">
                  <div class="flex items-center justify-center gap-2 mb-3">
                    <svg class="w-6 h-6 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M12,15.39L8.24,17.66L9.23,13.38L5.91,10.5L10.29,10.13L12,6.09L13.71,10.13L18.09,10.5L14.77,13.38L15.76,17.66M22,9.24L14.81,8.63L12,2L9.19,8.63L2,9.24L7.45,13.97L5.82,21L12,17.27L18.18,21L16.54,13.97L22,9.24Z"
                      />
                    </svg>
                    <p class="text-lg font-bold text-gray-800">Highest Score</p>
                  </div>
                  <div class="text-5xl font-black text-amber-600 mb-2 transition-all duration-300">
                    {{ Math.round(allTimeMetrics.highestScore ?? 0) }}%
                  </div>
                  <p class="text-sm text-gray-600">Your best performance across all tests</p>
                </div>
              </div>
            </div>

            <!-- Encouraging Message with Lightbulb Icon -->
            <div
              class="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border-2 border-blue-200 transition-all duration-300 hover:shadow-md"
            >
              <div class="flex items-start gap-3">
                <div
                  class="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center transition-all duration-300"
                >
                  <!-- Heroicons Lightbulb Icon -->
                  <svg
                    class="w-7 h-7 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h4 class="text-base font-bold text-blue-900 mb-2">
                    {{ allTimeMetrics.celebrationMessage }}
                  </h4>
                  <p class="text-sm text-gray-700 leading-relaxed mb-3">
                    {{ allTimeMetrics.encouragementMessage }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- ✅ REMOVED: "Not Available" warning - not student-friendly and causes confusion -->
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out;
}
</style>
